<template>
    <div>

        <v-row v-if="!canAddPaymentMehtod && $store.state.user.can_post_offers">
            <v-col class="text-center mt-5">
                <div>
                    <p>
                        You need to verify your name first.
                        <br />
                        You can verify your name by doing the following:
                    </p>
                    <p v-if="userCountry === 'NG'">
                        Linking your bank account and verifying your identity with your BVN under delivery method below
                    </p>
                    <p v-else-if="userCountry === 'GH'">
                        Linking your bank account under delivery method below
                    </p>
                    <template v-else>
                        <p>
                            Click the button below to verify your identity with your government issued identity.
                        </p>
                        <div class=" text-center">
                            <v-btn medium elevation="2" color="primary" class="ma-2 white--text" @click="$emit('verify')">
                                Verify
                                <v-icon right dark>mdi-account-clock</v-icon>
                            </v-btn>
                        </div>
                    </template>
                </div>
            </v-col>
        </v-row>

        <v-row
            v-if="canAddPaymentMehtod && !provider"
            class="align-content-center"
        >
            <v-col class="text-center">
                <p v-if="!loading">
                    There is no payment provider for your country
                </p>
                <v-progress-circular
                    v-if="loading"
                    indeterminate
                    color="primary"
                    class="align-content-center"
                />
            </v-col>
        </v-row>

        <v-row class="mt-5" v-if="canAddPaymentMehtod">
            <v-spacer />
            <p
                v-if="!loading && items.length === 0"
                class="body-2 grey--text text--lighten-1 mt-2 text-center"
            >
                No accounts currently linked.<br>Click the button below to add one.
            </p>
            <v-spacer />
        </v-row>
        <v-list two-line>
            <div
                v-for="(item, i) in items"
                :key="i"
            >
                <klarna-sepa-account
                    v-if="item.provider === 'KLARNA' && item.method === 'SEPA' && item.account"
                    :input="item.account"
                    :removable="true"
                    @remove="onRemove(i)"
                />
                <klarna-faster-account
                    v-if="item.provider === 'KLARNA' && item.method === 'FASTER' && item.account"
                    :input="item.account"
                    :removable="true"
                    @remove="onRemove(i)"
                />
                <plaid-account
                    v-if="item.provider === 'PLAID' && item.account"
                    :input="item.account"
                    :removable="true"
                    @updateLink="initUpdate(item)"
                    @remove="onRemove(i)"
                />
                <card-account
                    v-if="item.method === 'CARD' && item.account"
                    :input="item.account"
                    :removable="true"
                    @remove="onRemove(i)"
                />
                <worldremit-account
                    v-if="item.provider === 'WORLDREMIT' && item.account"
                    :input="item.account"
                    :removable="true"
                    @updateLink="initWorldRemitLogin"
                    @remove="onRemove(i)"
                />
                <kaoshi-test-account
                    v-if="isDevOrLocal && item.provider === 'KAOSHI_TEST_PROVIDER' && item.account"
                    :value="item.account"
                    :removable="true"
                    @remove="onRemove(i)"
                />
                <mono-account
                    v-if="isMonoAllowed && item.provider === 'MONO' && item.account"
                    :input="item.account"
                    :institutionName="item.institution_name"
                    :removable="true"
                    @updateLink="initMonoReauthorisation(item)"
                    @remove="onRemove(i)"
                />
            </div>
        </v-list>

        <v-col v-if="phoneAndAddressValid" class="text-center">
            <v-spacer />
            <klarna-link
                v-if="provider == 'KLARNA' && canAddPaymentMehtod"
                @loading="onLoading"
                @loaded="onLoaded"
                @onFinished="createPaymentMethod"
            >
                <v-icon>mdi-plus-thick</v-icon>
                Add bank account
            </klarna-link>
            <plaid-link
                ref="plaidLink"
                v-if="provider === 'PLAID'"
                :canAdd="canAddPaymentMehtod"
                :method="method"
                :country="country"
                :emailRequired="false"
                oauthRedirectURL="/plaid/redirect/payment-methods"
                @loading="onLoading"
                @loaded="onLoaded"
                @onFinished="createPaymentMethod"
                @updated="onUpdated"
            >
                <v-icon>mdi-plus-thick</v-icon>
                Add bank account
            </plaid-link>
            <card-form
                v-if="method === 'CARD' && canAddPaymentMehtod"
                @loading="onLoading"
                @loaded="onLoaded"
            ></card-form>
            <kaoshi-test-link
                ref="kaoshiTestLink"
                v-if="isDevOrLocal && provider === 'KAOSHI_TEST_PROVIDER' && canAddPaymentMehtod"
                method-variant="payment"
                @onFinished="createPaymentMethod"
            >
                <v-icon>mdi-plus-thick</v-icon>
            </kaoshi-test-link>

            <mono-link
                ref="monoLink"
                v-if="isMonoAllowed && !hasPaymentMethodByProvider('MONO')""
                :country="country"
                :allowBranches="allowBranches"
                @loading="onLoading"
                @loaded="onLoaded"
                @onFinished="createPaymentMethod"
                @updated="onUpdated"
            >
                <v-icon>mdi-plus-thick</v-icon>
                {{ country === 'NG' ? 'Domiciliary Account': 'Add Bank Account' }}
            </mono-link>
            <v-spacer />
        </v-col>

        <div class="align-content-center d-flex" v-if="!phoneAndAddressValid">
            <v-spacer />
            <v-btn
                class="text-none px-4 rounded-xl"
                color="#f5f5f5"
                @click="showContactDisclaimer"
            >
                <v-icon>mdi-plus-thick</v-icon>
                Add payment method
            </v-btn>
            <v-spacer />
        </div>

        <WorldremitLoginDialog
            ref="WorldremitLoginDialog"
        />

        <PaymentMethodAddedModal v-model="showPaymentMethodAddedModal" />
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    import ServerUtils from '../../services/ServerUtils';
    import KlarnaLink from './klarna-link.vue';
    import KlarnaSepaAccount from './bank-account-templates/klarna-sepa-account.vue';
    import KlarnaFasterAccount from './bank-account-templates/klarna-faster-account.vue';
    import PlaidLink from './plaid-link.vue';
    import MonoLink from './mono-link';
    import MonoAccount from './bank-account-templates/mono-account';
    import MonoService from '../../services/MonoService';
    import PlaidAccount from './bank-account-templates/plaid-account.vue';
    import CardAccount from './bank-account-templates/card.vue';
    import WorldremitAccount from './bank-account-templates/worldremit-account.vue';
    import WorldremitLoginDialog from './worldremit/LoginDialog.vue';
    import CardForm from './card-form.vue';
    import KaoshiTestAccount from './bank-account-templates/kaoshi-test-account.vue';
    import KaoshiTestLink from './deliveryMethods/kaoshi-test-method.vue';
    import PaymentMethodAddedModal from './settings/payment-method-added-modal.vue';

    export default {
        components: {
            'klarna-link': KlarnaLink,
            'klarna-sepa-account': KlarnaSepaAccount,
            'klarna-faster-account': KlarnaFasterAccount,
            'plaid-link': PlaidLink,
            'plaid-account': PlaidAccount,
            'card-account': CardAccount,
            'card-form': CardForm,
            MonoLink,
            MonoAccount,
            WorldremitAccount,
            WorldremitLoginDialog,
            KaoshiTestAccount,
            KaoshiTestLink,
            PaymentMethodAddedModal,
        },
        data() {
            return {
                loading: false,
                provider: null,
                method: null,
                items: [],
                country: null,
                plaidAccessToken: null,
                showPaymentMethodAddedModal: false,
            };
        },
        mounted() {
            this.getPaymentMethods();
        },
        computed: {
            ...mapState([
                'onboarding',
            ]),
            userCountry() {
              return this.$store.state.user.country_abbreviation;
            },
            isMonoAllowed () {
                if (this.userCountry === 'NG') {
                    return true;
                }
            },
            allowBranches() {
                const countries = [
                    'GH',
                    'TZ',
                    'UG',
                    'KE'
                ];
                return countries.indexOf(this.userCountry) >= 0;
            },
            canAddPaymentMehtod() {
                if (this.$store.state.user.can_post_offers === 1) {
                    return !!this.$store.state.user.name_verified_at;
                }

                return this.items.length === 0;
            },
            phoneAndAddressValid() {
                const {
                    phone,
                    address,
                    city,
                    state,
                    country,
                } = this.$store.state.user;

                const trigger = !phone
                    || !address
                    || !city
                    || !state
                    || !country;

                return !trigger;
            },
            isNeedToUpdateWR() {
              if (this.$route.query['update-wr']) {
                  return true;
              }

              return false;
            },
            isDevOrLocal() {
                return ServerUtils.isDevOrLocal();
            }
        },
        methods: {
            onLoading() {
                this.$emit('loading');
            },
            onLoaded() {
                this.$emit('loaded');
            },
            getPaymentMethods() {
                this.loading = true;
                Vue.backendApi().user(this.$store.state.user.id).getPaymentMethods()
                    .then((response) => {
                        this.loading = false;
                        this.method = response.data.method;
                        this.country = response.data.abbreviation;
                        if(response.data.items) {
                          response.data.items.forEach((item) => {
                            this.items.push(item);
                          })
                        }
                        this.provider = response.data.provider;

                        if (this.isNeedToUpdateWR) {
                            this.initWorldRemitLogin();
                        }
                    })
                    .catch((err) => {
                        this.loading = false;
                        this.$swal({
                            type: 'error',
                            title: 'Error loading payment method',
                            text: err,
                        });
                    });
            },
            hasPaymentMethodByProvider(provider) {
                return this.items.find((item) => item.provider === provider);
            },
            createPaymentMethod(payload) {
                this.$emit('loading');
                Vue.backendApi().user(this.$store.state.user.id)
                    .createPaymentMethod(payload)
                    .then((response) => {
                        this.$emit('loaded');
                        this.$emit('saved');
                        this.items.push(response.data);

                        const method = this.$store.state.user.can_post_offers === 1 ? 'debit card' : 'bank account';
                        const html = `<ul style="margin: 0 30px; line-height: 1.5; text-align: left;">
                                <li style="margin-bottom: 10px;">Now you can conduct transactions with this payment method on the Kaoshi network</li>
                                <li>You can disconnect your ${method} from Kaoshi anytime, by deleting your ${method} registration, as long as a transaction is not ongoing</li>
                            </ul>`;

                        if (this.onboarding) {
                            this.showPaymentMethodAddedModal = true;
                        } else {
                            this.$swal({
                                type: 'success',
                                title: 'Congratulations!',
                                html,
                            });
                        }
                    })
                    .catch((err) => {
                        this.$emit('loaded');
                        this.$swal({
                            type: 'error',
                            title: 'Error saving payment method',
                            text: err,
                        });
                    });
            },
            onRemove(index) {
                this.$swal.fire({
                    title: `You won't be able to revert this!`,
                    text: 'Are you sure you want to delete this payment method?',
                    type: 'warning',
                    showCancelButton: true,
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes',
                }).then((result) => {
                    if (result.value) {
                        this.deletePaymentMethod(index);
                    }
                });
            },
            deletePaymentMethod(index) {
                const paymentMethodId = this.items[index].id;
                this.$emit('loading');
                Vue.backendApi().user(this.$store.state.user.id)
                    .deletePaymentMethod(paymentMethodId)
                    .then((response) => {
                        this.$emit('loaded');
                        if(this.items[index]) {
                          this.items.splice(index, 1);

                            const paymentMethodType = this.$store.state.user.can_post_offers === 1 ? 'debit card' : 'bank account';
                            this.$swal({
                                type: 'success',
                                title: 'Yeah!',
                                text: `Your ${paymentMethodType} has been successfully removed from the Kaoshi App`,
                            });
                        }
                    })
                    .catch((err) => {
                        this.$emit('loaded');
                        this.$swal({
                            type: 'error',
                            title: 'Error deleting payment method',
                            text: err,
                        });
                    });
            },
            initUpdate(item) {
                const accessToken = item.api_data.access_token;
                if (!accessToken) {
                  return this.$swal({
                      type: 'error',
                      title: 'Missing public token',
                      text: err,
                  });
                }

                this.plaidAccessToken = accessToken;
                const interval = setInterval(() => {
                    if (this.$refs.plaidLink) {
                        this.$refs.plaidLink.init(accessToken, item.id);
                        clearInterval(interval);
                    }
                }, 10);
            },
            async initMonoReauthorisation(item) {
                const vm = this;
                const {
                    id,
                    account,
                } = item;
                const countryCode = this.$store.state.user.country_abbreviation;

                try {
                    this.$emit('loading');
                    const { data } = await Vue.backendApi().mono().reauthoriseAccount(account._id);
                    console.log('reauth data', data);
                    const { token } = data;

                    const connect = await MonoService.getConnect({
                        onSuccess: (data) => {
                            console.log('reauth success', data, id);
                            vm.onUpdated(id);
                        },
                        onEvent: (data) => {
                            console.log('reauth onEvent', data);
                        },
                    }, countryCode);

                    connect.reauthorise(token);
                    connect.open();
                } catch (err) {
                    this.$swal({
                        type: 'error',
                        title: 'Error',
                        text: err,
                    });
                } finally {
                    this.$emit('loaded');
                }
            },
            initWorldRemitLogin() {
                this.$refs.WorldremitLoginDialog.show();
            },
            onUpdated(id) {
                this.plaidAccessToken = null;
                this.$emit('loading');
                Vue.backendApi().user(this.$store.state.user.id)
                    .authenticatePaymentMethod(id)
                    .then((response) => {
                        this.$emit('loaded');
                        this.$swal({
                            type: 'success',
                            title: 'Account Successfully Re-authenticated',
                        });
                    })
                    .catch((err) => {
                        this.$emit('loaded');
                        this.$swal({
                            type: 'error',
                            title: 'Error updating payment method status',
                            text: err,
                        });
                    });
            },
            showContactDisclaimer() {
                this.$swal({
                    type: 'warning',
                    title: 'Attention!',
                    html: `<p style="line-height: 1.5em;">You have not registered your address and phone number!</p>
                        <p style="line-height: 1.5em;">Please do this before creating a payment method</p>`
                });
                this.$emit('scroll-to-address', 'payment-method');
            },
        },
    };
</script>
